import type { ClaimInviteRewardsDto, InviteSummaryDto } from "../api/dto/friends.dto.js";
import { claimInviteRewards, getInvite } from "../api/requests/friends.requests.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { LocalizationManager } from "../localization/localization_manager.js";

export class InviteService
{
  private static summary: InviteSummaryDto | null = null;
  private static inflight: Promise<InviteSummaryDto | null> | null = null;
  private static claiming: Promise<ClaimInviteRewardsDto | null> | null = null;

  private constructor() {}

  public static get(): InviteSummaryDto | null
  {
    return InviteService.summary;
  }

  public static refresh(): Promise<InviteSummaryDto | null>
  {
    if ( InviteService.inflight )
      return InviteService.inflight;

    InviteService.inflight = getInvite()
      .then( summary =>
      {
        InviteService.summary = summary;
        return summary;
      } )
      .catch( e =>
      {
        console.warn( "[Invite] Failed to load invite summary", e );
        return InviteService.summary;
      } )
      .finally( () => { InviteService.inflight = null; } );

    return InviteService.inflight;
  }

  public static link( summary: InviteSummaryDto ): string
  {
    if ( summary.link )
      return summary.link;

    return `${window.location.origin}${window.location.pathname}?startapp=${encodeURIComponent( summary.start_param )}`;
  }

  public static async gameLink(): Promise<string | null>
  {
    const summary = InviteService.get() ?? await InviteService.refresh();
    return summary ? InviteService.link( summary ) : null;
  }

  public static async share(): Promise<void>
  {
    const summary = InviteService.get() ?? await InviteService.refresh();
    if ( !summary )
    {
      console.warn( "[Invite] Invite is not available" );
      return;
    }

    if ( !summary.link )
      console.warn( "[Invite] Server has no Telegram bot configured, sharing the web link" );

    TelegramAPISettings.shareLink( InviteService.link( summary ), LocalizationManager.t( "INVITE_FRIEND.SHARE_TEXT" ) );
  }

  public static claim(): Promise<ClaimInviteRewardsDto | null>
  {
    if ( InviteService.claiming )
      return InviteService.claiming;

    InviteService.claiming = ( async () =>
    {
      const summary = await InviteService.refresh();
      if ( !summary || summary.unclaimed_count === 0 )
        return null;

      const result = await claimInviteRewards();
      if ( result.player )
        adoptEconomyFromPlayer( result.player );

      InviteService.summary = { ...summary, unclaimed_count: 0, unclaimed_coins: 0 };
      return result.coins > 0 ? result : null;
    } )()
      .catch( e =>
      {
        console.warn( "[Invite] Failed to claim invite rewards", e );
        return null;
      } )
      .finally( () => { InviteService.claiming = null; } );

    return InviteService.claiming;
  }
}
